import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from 'react-query';
import AuthContext from './AuthProvider';
import { registerPushServiceWorker, showForegroundNotification } from '../utils/pushNotifications';

const SocketContext = createContext({ connected: false });

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const SocketProvider = ({ children }) => {
    const { auth } = useContext(AuthContext);
    const token = auth?.userInfo?.token || null;
    const queryClient = useQueryClient();
    const socketRef = useRef(null);
    const [connected, setConnected] = useState(false);

    // Register the push service worker once so a browser that previously opted
    // in keeps receiving background push after a redeploy. (Subscribing itself
    // is an explicit opt-in handled by the toggle.)
    useEffect(() => {
        registerPushServiceWorker();
    }, []);

    useEffect(() => {
        if (!token || !BACKEND_URL) {
            return undefined;
        }

        const socket = io(BACKEND_URL, {
            auth: { token },
            transports: ['websocket', 'polling'],
            withCredentials: true,
            reconnectionAttempts: 10,
        });
        socketRef.current = socket;

        socket.on('connect', () => setConnected(true));
        socket.on('disconnect', () => setConnected(false));
        socket.on('connect_error', () => setConnected(false));

        socket.on('notification:new', (payload = {}) => {
            // Refresh the inbox-backed views instantly.
            queryClient.invalidateQueries('inbox');
            queryClient.invalidateQueries('notifications');
            queryClient.invalidateQueries('broadcast-inbox');

            // Foreground OS notification (the SW covers the closed-tab case).
            showForegroundNotification({
                title: payload.title || 'New notification',
                body: payload.body || '',
                url: payload.link || '/admin/my-notifications',
            });
        });

        return () => {
            socket.off('notification:new');
            socket.disconnect();
            socketRef.current = null;
            setConnected(false);
        };
    }, [token, queryClient]);

    return (
        <SocketContext.Provider value={{ connected }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = () => useContext(SocketContext);

export default SocketContext;
